/**
 * 跟随状态机 —— "机体在动，视野要不要跟着动"。
 *
 * ## 选择：显式开关 + 用户操作后**自动暂停**（并在界面上说出来）
 *
 * 三种可选做法及它们的实际后果：
 *
 * 1. **强制跟随**：用户想看一眼刚才飞过的弯道，手指还没松开视野就被拽回去。
 *    野外检查地形时这是不能用的。
 * 2. **纯手动跟随**（不自动暂停，只有开关）：用户必须记得自己去关开关。
 *    而"忘了关"的代价是下一个航点时视野突然跳走 —— 比第一种更糟，
 *    因为它发生在用户以为自己在控制的时候。
 * 3. **用户操作即暂停 + 一键恢复**（本实现）：`dragstart` / `zoomstart` 这类
 *    **由用户手势触发**的相机变化会把跟随置为暂停，显示"已暂停跟随（手动平移）"
 *    并提供"定位到机体"按钮。遥测更新永远不会在用户正在看别处时抢走视野。
 *
 * 关键细节：暂停只能由**用户手势**触发。我们自己调的 `jumpTo`/`fitBounds`
 * 也会发 `movestart`，若不加区分，第一次自动跟随就会把自己暂停掉 ——
 * 这是这一套逻辑里最容易写错的地方，所以事件入口只有一个
 * （`fromUserGesture`），判定也放在这一层用测试钉住。
 *
 * 换机体时回到"跟随"：新机体的位置对用户来说是未知的，
 * 而"跟随一个未知位置"至少能立刻把视野带过去；保持暂停则会让新机体在屏幕外，
 * 用户的第一反应是"没数据"。切换机体的行为因此必须写死在状态机里，而不是留给调用方。
 */

import { haversineDistanceM, type GeoBounds, type LatLon } from '@hgc/ardupilot';

export type FollowMode = 'following' | 'paused';

/** 暂停的原因。界面文案由它决定 —— "为什么视野不跟着动了"必须有答案。 */
export type FollowPauseReason =
  /** 用户拖动/缩放了地图 */
  | 'user-gesture'
  /** 用户点了"全览航迹" */
  | 'overview'
  /** 用户自己关了跟随开关 */
  | 'user-toggle';

export interface FollowState {
  readonly mode: FollowMode;
  readonly pausedReason: FollowPauseReason | null;
}

export const INITIAL_FOLLOW_STATE: FollowState = Object.freeze({
  mode: 'following',
  pausedReason: null,
});

export type FollowEvent =
  | { readonly type: 'user-gesture' }
  | { readonly type: 'set-follow'; readonly follow: boolean }
  | { readonly type: 'locate' }
  | { readonly type: 'overview' }
  | { readonly type: 'vehicle-changed' };

export function followReducer(state: FollowState, event: FollowEvent): FollowState {
  switch (event.type) {
    case 'user-gesture':
      // 已经暂停就不改原因：第一次暂停的原因才是用户真正做的那件事
      return state.mode === 'paused' ? state : { mode: 'paused', pausedReason: 'user-gesture' };
    case 'set-follow':
      return event.follow
        ? INITIAL_FOLLOW_STATE
        : { mode: 'paused', pausedReason: 'user-toggle' };
    case 'locate':
      return INITIAL_FOLLOW_STATE;
    case 'overview':
      return { mode: 'paused', pausedReason: 'overview' };
    case 'vehicle-changed':
      return INITIAL_FOLLOW_STATE;
    default:
      return state;
  }
}

/**
 * `movestart` 这类事件要不要当成"用户操作"。
 *
 * MapLibre 会带上 `originalEvent`（真实的鼠标/触摸/滚轮事件）—— 有它才是用户做的。
 * 没有它说明这次相机移动是我们自己发起的（`jumpTo`/`easeTo`/`fitBounds`）。
 */
export function fromUserGesture(originalEvent: unknown): boolean {
  return originalEvent !== undefined && originalEvent !== null;
}

/** 跟随状态对应的界面文案（中文，面向用户）。`null` 表示"正在跟随，不用特别说明"。 */
export function followHint(state: FollowState): string | null {
  if (state.mode === 'following') return null;
  switch (state.pausedReason) {
    case 'user-gesture':
      return '已暂停跟随（你手动移动了地图）';
    case 'overview':
      return '已暂停跟随（正在全览航迹）';
    case 'user-toggle':
      return '已暂停跟随（跟随开关已关闭）';
    default:
      return '已暂停跟随';
  }
}

/** 默认死区比例：机体离屏幕中心的距离超过视口短边的这个比例时才重新居中。 */
export const DEFAULT_DEAD_ZONE_FRACTION = 0.25;

/**
 * 视野里"每米对应多少度"的具体化：给出缓冲盒在中心纬度上的宽高（米）。
 *
 * 用 haversine 而不是"度 × 常数"：在中纬度上经度每度的实际长度只有赤道的
 * 0.7 倍左右，用常数会让死区在高纬度莫名变大。
 */
export function viewportSpanM(bounds: GeoBounds): { readonly widthM: number; readonly heightM: number } {
  const centerLat = (bounds.southDeg + bounds.northDeg) / 2;
  const centerLon = bounds.crossesAntimeridian
    ? normalizeLongitude((bounds.westDeg + bounds.eastDeg + 360) / 2)
    : (bounds.westDeg + bounds.eastDeg) / 2;
  return {
    widthM: Math.abs(
      haversineDistanceM(
        { latDeg: centerLat, lonDeg: bounds.westDeg },
        { latDeg: centerLat, lonDeg: bounds.eastDeg },
      ),
    ),
    heightM: Math.abs(
      haversineDistanceM(
        { latDeg: bounds.southDeg, lonDeg: centerLon },
        { latDeg: bounds.northDeg, lonDeg: centerLon },
      ),
    ),
  };
}

function normalizeLongitude(lonDeg: number): number {
  return ((lonDeg % 360) + 540) % 360 - 180;
}

/**
 * 机体是否已经漂到死区之外（需要重新居中）。
 *
 * 为什么要有死区：机体悬停时位置在几米内抖动，如果每次都 `easeTo` 到机体，
 * 地图会以同样的频率轻微晃动 —— 看起来像界面在抖，实际上是相机在追噪声。
 * 死区让"机体在屏幕中间附近"时**完全不动相机**。
 */
export function shouldRecenter(
  vehicle: LatLon,
  bounds: GeoBounds,
  deadZoneFraction = DEFAULT_DEAD_ZONE_FRACTION,
): boolean {
  const span = viewportSpanM(bounds);
  const deadZoneM = Math.max(1, Math.min(span.widthM, span.heightM) * deadZoneFraction);
  const center: LatLon = {
    latDeg: clampLatitude((bounds.southDeg + bounds.northDeg) / 2),
    lonDeg: bounds.crossesAntimeridian
      ? normalizeLongitude((bounds.westDeg + bounds.eastDeg + 360) / 2)
      : (bounds.westDeg + bounds.eastDeg) / 2,
  };
  return haversineDistanceM(vehicle, center) > deadZoneM;
}

function clampLatitude(latDeg: number): number {
  return latDeg < -90 ? -90 : latDeg > 90 ? 90 : latDeg;
}

/** "定位到机体"应当在哪个缩放层停下。 */
export const LOCATE_TARGET_ZOOM = 16;

/**
 * 定位时的缩放选择。
 *
 * 用户如果已经放大到 17 层在看清某个院子，点了"定位"就把他拉到 16 层是**降级**；
 * 用户如果停留在世界视图，定位过去仍然停在 1.6 层等于什么都没发生。
 * 所以：比 `LOCATE_TARGET_ZOOM` 更近就保持不动，更远则拉到目标层，
 * 并始终夹在底图源自己的缩放范围内（否则会出现 18 层源上的 19 层空瓦片）。
 */
export function locateZoom(
  currentZoom: number,
  zoomRange: { readonly min: number; readonly max: number },
): number {
  const target = Number.isFinite(currentZoom) ? Math.max(currentZoom, LOCATE_TARGET_ZOOM) : LOCATE_TARGET_ZOOM;
  return Math.min(Math.max(target, zoomRange.min), zoomRange.max);
}

/**
 * 包围盒 → MapLibre 的 `[[西, 南], [东, 北]]`。
 *
 * 跨 180° 经线时东边界写成 `east + 360`：MapLibre 的世界坐标是连续的，
 * 这样写得到的是"跨经线的那一小段"；写成 `(west, east)` 数值对会得到
 * 横跨整个地球的视野 —— 一个看起来能用、实际上完全错的取景。
 */
export function toLngLatCorners(bounds: GeoBounds): [[number, number], [number, number]] {
  const east = bounds.crossesAntimeridian ? bounds.eastDeg + 360 : bounds.eastDeg;
  return [
    [bounds.westDeg, bounds.southDeg],
    [east, bounds.northDeg],
  ];
}
